
import { ISchedule, IScheduleEntry } from '@app-types/websocket/schedule.types';
import { createArrayCopyAndSort, createScheduleSide, findFirstAndLastEntry, makeSideToMatch, mergeAndClean, } from '../ScheduleCalc';
import { addDays, isSameDay } from '@helpers/DateHelper';


const swapParent = (schedule:ISchedule, year:number,month:number,date:number, swap:"MORNING"|"NIGHT", 
  originalDay?:Date):ISchedule => {

  // if originalDay is set we are dragging to a new max. morning = to the left, night = to the right of original Day
  // so lets not swap the schedule if the morning/night is already correct
  if (originalDay !== undefined ) {

    const oDate = new Date(originalDay.getTime()); 
    const nDate = new Date(year,month,date,0,0,0,1); // the date we are dragging to
    if (swap === "MORNING") { // we are clicking morning so lets check night
      oDate.setHours(23,0,0,0);
      nDate.setHours(5,0,0,0);
    } else {
      oDate.setHours(5,0,0,0);
      nDate.setHours(23,0,0,0);
    }
    const oDateTime = oDate.getTime();
    const nDateTime = nDate.getTime();

    let oParent:"A"|"B"|undefined ;
    let nParent:"A"|"B"|undefined ;

    for (const entry of Object.values(schedule.data.scheduleEntriesParentA)) {
      if (oParent === undefined && entry.startTime < oDateTime && entry.endTime > oDateTime) {
        oParent = "A";
        if (!isSameDay(oDate, new Date(entry.startTime)) && !isSameDay(oDate, new Date(entry.endTime))) {
          if (isSameDay(oDate, nDate)) {
            oParent = undefined;
          }
        }
      }
      if (nParent === undefined && entry.startTime < nDateTime && entry.endTime > nDateTime) {
        nParent = "A";
      }
    }

    for (const entry of Object.values(schedule.data.scheduleEntriesParentB)) {
      if (oParent === undefined && entry.startTime < oDateTime && entry.endTime > oDateTime) {
        oParent = "B";
        if (!isSameDay(oDate, new Date(entry.startTime)) && !isSameDay(oDate, new Date(entry.endTime))) {
          if (isSameDay(oDate, nDate)) {
            oParent = undefined;
          }
        }
      }
      if (nParent === undefined && entry.startTime < nDateTime && entry.endTime > nDateTime) {
        nParent = "B";
      }
    }

    if (oParent !== undefined && oParent === nParent) {
      return schedule;
    }
  }

  const newSchedule:ISchedule = {
    id:schedule.id,
    lastHandover:schedule.lastHandover,
    status:schedule.status,
    name:schedule.name,
    school:schedule.school,
    data:{
      children:schedule.data.children,
      parentA:schedule.data.parentA,
      parentB:schedule.data.parentB,
      scheduleEntriesParentA:{},
      scheduleEntriesParentB:{},
    }
  }

  const modifiedEntries = mergeAndClean(quickSwap(createArrayCopyAndSort(schedule.data.scheduleEntriesParentA),year,month,date, swap));
  const foundEntries = findFirstAndLastEntry(schedule.data.scheduleEntriesParentB);
  const matchingEntries = makeSideToMatch(modifiedEntries, foundEntries.firstEntry, foundEntries.lastEntry);
  newSchedule.data.scheduleEntriesParentA = createScheduleSide(modifiedEntries);
  newSchedule.data.scheduleEntriesParentB = createScheduleSide(matchingEntries);

  return newSchedule
}

const shiftSpecificDay = (schedule:ISchedule, shiftDays:number, day:Date, swipdeStartSide:"LEFT"|"RIGHT"):ISchedule => {

    const newSchedule:ISchedule = {
      id:schedule.id,
      lastHandover:schedule.lastHandover,
      status:schedule.status,
      name:schedule.name,
      school:schedule.school,
      data:{
        children:schedule.data.children,
        parentA:schedule.data.parentA,
        parentB:schedule.data.parentB,
        scheduleEntriesParentA:{},
        scheduleEntriesParentB:{},
      }
    }
    const start = new Date(day);
    start.setHours(0,0,0,0);
    let startOfDay = start.getTime();
    const end = addDays(start, 1);
    let endOfDay = end.getTime();

    const baseData = createArrayCopyAndSort(schedule.data.scheduleEntriesParentA);
    const baseBData = createArrayCopyAndSort(schedule.data.scheduleEntriesParentB);
    const lenA = baseData ? baseData.length : 0;
    const lenB = baseBData ? baseBData.length : 0;

    if (lenA === 0 && lenB === 0) {
      console.log("click on empty schedule");
    } else {
      // Grab boundary times in O(1). Use sentinels to avoid conditionals later.
      const aStart = lenA ? baseData[0].startTime :  Infinity;
      const bStart = lenB ? baseBData[0].startTime :  Infinity;
      const aEnd   = lenA ? baseData[lenA - 1].endTime : -Infinity;
      const bEnd   = lenB ? baseBData[lenB - 1].endTime : -Infinity;

      const aStartLocation:string|boolean|undefined = lenA ? baseData[0].startAtSchool? true: baseData[0].startLoc:  undefined;
      const bStartLocation:string|boolean|undefined = lenB ? baseBData[0].startAtSchool? true: baseBData[0].startLoc :  undefined;
      const aEndLocation:string|boolean|undefined   = lenA ? baseData[lenA - 1].endAtSchool? true: baseData[0].endLoc : undefined;
      const bEndLocation:string|boolean|undefined   = lenB ? baseBData[lenB - 1].endAtSchool? true: baseBData[0].endLoc : undefined;

      // Earliest start + owner
      let earliestStart = aStart;
      let startOwner = schedule.data.parentA;
      let startLoc:string|boolean|undefined  = aStartLocation;
      if (bStart < earliestStart) { earliestStart = bStart; startOwner = schedule.data.parentB; startLoc = bStartLocation}

      // Latest end + owner
      let latestEnd = aEnd;
      let endOwner = schedule.data.parentA;
      let endLoc:string|boolean|undefined  = aEndLocation;
      if (bEnd > latestEnd) { latestEnd = bEnd; endOwner = schedule.data.parentB; endLoc = bEndLocation}

      if (endOfDay <= earliestStart) {
        console.log("clicked before start of schedule " + startOwner);
        if (shiftDays >= 0 ) {
          const startLocal = new Date(day);
          startLocal.setHours(12,0,0,0);

          const draggedDay = addDays(startLocal, shiftDays);
          let endLocal = earliestStart;
          if (draggedDay.getTime() > endLocal) {
            endLocal = draggedDay.getTime();
          }

          return addQuickSleep(schedule, startLocal.getTime(), endLocal, true, (startLoc === true), undefined, 
            (typeof startLoc === "string"?startLoc:undefined), otherParent(schedule, startOwner));
        } else {
          const endDay = new Date(day);
          endDay.setHours(12,0,0,0);
          const startDay = addDays(endDay, shiftDays);
          return addQuickSleep(schedule, startDay.getTime(), endDay.getTime(), true, true, undefined, undefined, startOwner);
        }
      }

      if (startOfDay >= latestEnd) {
        console.log("clicked after end of schedule " + endOwner);
        if (shiftDays <= 0 ) {
          const endLocal = new Date(day);
          endLocal.setHours(12,0,0,0);

          const draggedDay = addDays(endLocal, shiftDays);
          let startLocal = latestEnd;
          if (draggedDay.getTime() < startLocal) {
            startLocal = draggedDay.getTime();
          }

          return addQuickSleep(schedule, startLocal, endLocal.getTime(), (endLoc === true), true, (typeof endLoc === "string"?endLoc:undefined), 
            undefined, otherParent(schedule, endOwner));
        } else {
          const begin = new Date(day);
          begin.setHours(12,0,0,0);
          const endDay = addDays(begin, shiftDays);
          return addQuickSleep(schedule, begin.getTime(), endDay.getTime(), true, true, undefined, undefined, endOwner);
        }
      }

      // Start boundary falls on this day: [startOfDay, endOfDay)
      if (earliestStart >= startOfDay-(1000*60*60*12) && earliestStart < endOfDay) {
        console.log("clicked start of schedule " + startOwner);
        if (swipdeStartSide === "RIGHT" || (swipdeStartSide === "LEFT" && earliestStart <= startOfDay)) {
          return moveFirstEntry(schedule,shiftDays,startOwner)
        } else {

          if (shiftDays >= 0 ) {
            const startLocal = new Date(day);
            startLocal.setHours(12,0,0,0);
            const endLocal = addDays(startLocal, shiftDays);
            return addQuickSleep(schedule, startLocal.getTime(), endLocal.getTime(), true, true, 
              undefined, undefined, otherParent(schedule, startOwner));
          } else {
            const endLocal = new Date(day);
            endLocal.setHours(12,0,0,0);
            const beginLocal = addDays(endLocal, shiftDays);
            return addQuickSleep(schedule, beginLocal.getTime(), endLocal.getTime(), true, true, 
              undefined, undefined, otherParent(schedule, startOwner));
          }
        }
      }

      // End boundary falls on this day: (startOfDay, endOfDay]
      if (latestEnd > startOfDay && latestEnd <= endOfDay+(1000*60*60*12)) {
        console.log("clicked end of schedule " + endOwner);
        if (swipdeStartSide === "LEFT" || (swipdeStartSide === "RIGHT" && latestEnd >= endOfDay)) {
          return moveLastEntry(schedule,shiftDays,endOwner)
        } else {
          console.log("add days for other");

          if (shiftDays <= 0 ) {
            const endLocal = new Date(day);
            endLocal.setHours(12,0,0,0);
            const startLocal = addDays(endLocal, shiftDays);
            return addQuickSleep(schedule, startLocal.getTime(), endLocal.getTime(), true, true, 
              undefined, undefined, otherParent(schedule, endOwner));
          } else {
            const begin = new Date(day);
            begin.setHours(12,0,0,0);
            const endDay = addDays(begin, shiftDays);
            return addQuickSleep(schedule, begin.getTime(), endDay.getTime(), true, true, undefined, undefined, otherParent(schedule, endOwner));
          }

          
        }
      }
    }

    
    
    if (handovers(baseData, startOfDay, endOfDay) === 0) {
      if (swipdeStartSide === "LEFT") {
        startOfDay = addDays(start, -1).getTime();
        endOfDay = addDays(end, -1).getTime();
        const handoverCount = handovers(baseData, startOfDay, endOfDay);
        const begin = new Date(day);
        begin.setHours(12,0,0,0);
        
        if (handoverCount === 0) {
          const endDay = addDays(begin, shiftDays);
          const parentId = hasCustody(baseData, begin.getTime())?schedule.data.parentB:schedule.data.parentA;
          const startTime = begin.getTime() < endDay.getTime()? begin.getTime() : endDay.getTime();
          const endTime = begin.getTime() > endDay.getTime()? begin.getTime() : endDay.getTime();
          return addQuickSleep(schedule, startTime, endTime, true, true, undefined, undefined, parentId);
        } else if (handoverCount > 1) {
          let parentId = schedule.data.parentA
          let beginAdd:Date;
          let endDay:Date;

          if (shiftDays < 0) {
            beginAdd = begin;
            endDay = addDays(begin, shiftDays);
            parentId = hasCustody(baseData, begin.getTime())?schedule.data.parentA:schedule.data.parentB;
          } else {
            beginAdd = addDays(begin, -1);
            endDay = addDays(begin, shiftDays-1);
            parentId = hasCustody(baseData, begin.getTime())?schedule.data.parentB:schedule.data.parentA;
          }
          const startTime = beginAdd.getTime() < endDay.getTime()? beginAdd.getTime() : endDay.getTime();
          const endTime = beginAdd.getTime() > endDay.getTime()? beginAdd.getTime() : endDay.getTime();
          return addQuickSleep(schedule, startTime, endTime, true, true, undefined, undefined, parentId);
        }
      } else {
        startOfDay = addDays(start, 1).getTime();
        endOfDay = addDays(end, 1).getTime();
        const handoverCount = handovers(baseData, startOfDay, endOfDay);
        const begin = new Date(day);
        begin.setHours(12,0,0,0);

        if (handoverCount === 0) {

          const endDay = addDays(begin, shiftDays);
          const parentId = hasCustody(baseData, begin.getTime())?schedule.data.parentB:schedule.data.parentA;
          const startTime = begin.getTime() < endDay.getTime()? begin.getTime() : endDay.getTime();
          const endTime = begin.getTime() > endDay.getTime()? begin.getTime() : endDay.getTime();
          return addQuickSleep(schedule, startTime, endTime, true, true, undefined, undefined, parentId);
        } else if (handoverCount > 1) {
          let parentId = schedule.data.parentA
          if (shiftDays > 0) {
            parentId = hasCustody(baseData, begin.getTime())?schedule.data.parentA:schedule.data.parentB;
          } else {
            parentId = hasCustody(baseData, begin.getTime())?schedule.data.parentB:schedule.data.parentA;
          }
          const beginAdd = addDays(begin, 1);
          const endDay = addDays(begin, shiftDays+1);
          const startTime = beginAdd.getTime() < endDay.getTime()? beginAdd.getTime() : endDay.getTime();
          const endTime = beginAdd.getTime() > endDay.getTime()? beginAdd.getTime() : endDay.getTime();
          return addQuickSleep(schedule, startTime, endTime, true, true, undefined, undefined, parentId);
        }
      }
    }

    const firstEditTime:number = shiftDays > 0 ? startOfDay : addDays(start, shiftDays).getTime();
    const lastEditTime:number = shiftDays > 0 ? addDays(end, shiftDays).getTime() : endOfDay;
  
    const modifiedEntries = mergeAndClean(shiftDay(baseData, startOfDay, endOfDay, firstEditTime, lastEditTime, shiftDays));
    const foundEntries = findFirstAndLastEntry(schedule.data.scheduleEntriesParentB);
    const matchingEntries = makeSideToMatch(modifiedEntries, foundEntries.firstEntry, foundEntries.lastEntry);
    newSchedule.data.scheduleEntriesParentA = createScheduleSide(modifiedEntries);
    newSchedule.data.scheduleEntriesParentB = createScheduleSide(matchingEntries);
  
    return newSchedule;
}


const addSleep = (schedule:ISchedule, from:number, to:number, parentId:number):ISchedule => {
  return schedule;
}


const handovers = (side:IScheduleEntry[], start:number, end:number):number => {
  let handoverCount = 0;
  for (const entry of side) {
    if (entry.startTime > start && entry.startTime < end) { 
      handoverCount += 1;
    }
    if (entry.endTime > start && entry.endTime < end) { 
      handoverCount += 1;
    }
  }
  return handoverCount;
}

const hasCustody = (side:IScheduleEntry[], time:number):boolean => {
  for (const entry of side) {
    if (entry.startTime < time && entry.endTime > time) { 
      return true;
    }
  }
  return false
}

const otherParent = (schedule:ISchedule, parent:number):number => {
  if (schedule.data.parentA === parent) {
    return schedule.data.parentB;
  }
  return schedule.data.parentA;
}

const shiftDay = (side:IScheduleEntry[], startOfDay:number, endOfDay:number, firstEditTime:number, 
  lastEditTime:number, shiftDays:number):IScheduleEntry[] => {

  for (const entry of side) {
    if (entry.startTime > startOfDay && entry.startTime < endOfDay) { 
      entry.startTime = addDays(new Date(entry.startTime), shiftDays).getTime();
    }
    if (entry.endTime > startOfDay && entry.endTime < endOfDay) { 
      entry.endTime = addDays(new Date(entry.endTime), shiftDays).getTime();
    }
  }

  side.sort((a,b) => a.startTime - b.startTime);

  return side;
}

const addQuickSleep = (schedule:ISchedule, start:number, end:number, startAtSchool:boolean, endAtSchool:boolean, 
  startLoc:string|undefined, endLoc:string|undefined, parentId:number):ISchedule => {

  const newSchedule:ISchedule = {
    id:schedule.id,
    lastHandover:schedule.lastHandover,
    status:schedule.status,
    name:schedule.name,
    school:schedule.school,
    data:{
      children:schedule.data.children,
      parentA:schedule.data.parentA,
      parentB:schedule.data.parentB,
      scheduleEntriesParentA:{},
      scheduleEntriesParentB:{},
    }
  }


  if (schedule.data.parentA === parentId) {

    const modifiedEntries = mergeAndClean(addSleepTime(createArrayCopyAndSort(schedule.data.scheduleEntriesParentA),
      start, end, startAtSchool, endAtSchool, startLoc, endLoc));
    const foundEntries = findFirstAndLastEntry(schedule.data.scheduleEntriesParentB);
    const matchingEntries = makeSideToMatch(modifiedEntries, foundEntries.firstEntry, foundEntries.lastEntry);
    newSchedule.data.scheduleEntriesParentA = createScheduleSide(modifiedEntries);
    newSchedule.data.scheduleEntriesParentB = createScheduleSide(matchingEntries);
  } else {
    const modifiedEntries = mergeAndClean(addSleepTime(createArrayCopyAndSort(schedule.data.scheduleEntriesParentB),
      start, end, startAtSchool, endAtSchool, startLoc, endLoc));
    const foundEntries = findFirstAndLastEntry(schedule.data.scheduleEntriesParentA);
    const matchingEntries = makeSideToMatch(modifiedEntries, foundEntries.firstEntry, foundEntries.lastEntry);
    newSchedule.data.scheduleEntriesParentA = createScheduleSide(matchingEntries);
    newSchedule.data.scheduleEntriesParentB = createScheduleSide(modifiedEntries);
  }

  return newSchedule;
}

const moveLastEntry = (schedule:ISchedule, days:number, parentId:number):ISchedule => {

  const newSchedule:ISchedule = {
    id:schedule.id,
    lastHandover:schedule.lastHandover,
    status:schedule.status,
    name:schedule.name,
    school:schedule.school,
    data:{
      children:schedule.data.children,
      parentA:schedule.data.parentA,
      parentB:schedule.data.parentB,
      scheduleEntriesParentA:{},
      scheduleEntriesParentB:{},
    }
  }


  if (schedule.data.parentA === parentId) {
    const modifiedEntries = mergeAndClean(moveLastEntryI(createArrayCopyAndSort(schedule.data.scheduleEntriesParentA),days));
    newSchedule.data.scheduleEntriesParentA = createScheduleSide(modifiedEntries);
    newSchedule.data.scheduleEntriesParentB = schedule.data.scheduleEntriesParentB;
  } else {
    const modifiedEntries = mergeAndClean(moveLastEntryI(createArrayCopyAndSort(schedule.data.scheduleEntriesParentB),days));
    newSchedule.data.scheduleEntriesParentB = createScheduleSide(modifiedEntries);
    newSchedule.data.scheduleEntriesParentA = schedule.data.scheduleEntriesParentA;
  }

  return newSchedule;
}
const moveLastEntryI = (side:IScheduleEntry[], days:number):IScheduleEntry[] => {
  if (side.length === 0) {
    return side;
  }
  const newEndTime =  addDays(new Date(side[side.length-1].endTime), days);

  if (isSameDay(newEndTime, new Date(side[side.length-1].endTime))) {
    return side.slice(0,side.length-1);
  } else {
    side[side.length-1].endTime = newEndTime.getTime();
    return side;
  }
}

const moveFirstEntry = (schedule:ISchedule, days:number, parentId:number):ISchedule => {

  const newSchedule:ISchedule = {
    id:schedule.id,
    lastHandover:schedule.lastHandover,
    status:schedule.status,
    name:schedule.name,
    school:schedule.school,
    data:{
      children:schedule.data.children,
      parentA:schedule.data.parentA,
      parentB:schedule.data.parentB,
      scheduleEntriesParentA:{},
      scheduleEntriesParentB:{},
    }
  }


  if (schedule.data.parentA === parentId) {
    const modifiedEntries = mergeAndClean(moveFirstEntryI(createArrayCopyAndSort(schedule.data.scheduleEntriesParentA),days));
    newSchedule.data.scheduleEntriesParentA = createScheduleSide(modifiedEntries);
    newSchedule.data.scheduleEntriesParentB = schedule.data.scheduleEntriesParentB;
  } else {
    const modifiedEntries = mergeAndClean(moveFirstEntryI(createArrayCopyAndSort(schedule.data.scheduleEntriesParentB),days));
    newSchedule.data.scheduleEntriesParentB = createScheduleSide(modifiedEntries);
    newSchedule.data.scheduleEntriesParentA = schedule.data.scheduleEntriesParentA;
  }

  return newSchedule;
}
const moveFirstEntryI = (side:IScheduleEntry[], days:number):IScheduleEntry[] => {
  if (side.length === 0) {
    return side;
  }
  const newStartTime =  addDays(new Date(side[0].startTime), days);

  if (isSameDay(newStartTime, new Date(side[0].startTime))) {
    return side.slice(1,side.length);
  } else {
    side[0].startTime = newStartTime.getTime();
    return side;
  }
}

const addSleepTime = (side:IScheduleEntry[], start:number, end:number, startAtSchool:boolean, endAtSchool:boolean, 
  startLoc:string|undefined, endLoc:string|undefined):IScheduleEntry[] => {

  let id = 1;
  if (side.length > 0) {
    id = side.reduce((prev, current) =>  ((prev && prev.id > current.id) ? prev : current)).id+1;
  }

  side.push({
    id,
    startTime:start,
    startAtSchool,
    startLoc,
    endTime:end,
    endAtSchool,
    endLoc,
  });

  side.sort((a,b) => a.startTime - b.startTime);

  return side;
}

const hasChildAtTime = (parent:number, schedule:ISchedule|undefined, time:number, position:"START"|"END"):number|null => {
  let entries:IScheduleEntry[] = [];
  if (schedule !== undefined && schedule.data.parentA === parent) {
    entries = createArrayCopyAndSort(schedule.data.scheduleEntriesParentA);
  } else if (schedule !== undefined && schedule.data.parentB === parent) {
    entries = createArrayCopyAndSort(schedule.data.scheduleEntriesParentB);
  }

  for (const entry of entries) {
    if (entry.startTime < time && entry.endTime > time) {
      if (position === "START") {return entry.startTime};
      if (position === "END") {return entry.endTime};
    }
    if (entry.startTime > time) {
      return null;
    }
  }

  return null;
}

const applyToAllEntries = (schedule:ISchedule, parentId:number, onDay:Date, 
  alter:"START"|"END", time:number, loc:string, atSchool:boolean):ISchedule => {

  const newSchedule:ISchedule = {
    id:schedule.id,
    lastHandover:schedule.lastHandover,
    status:schedule.status,
    name:schedule.name,
    school:schedule.school,
    data:{
      children:schedule.data.children,
      parentA:schedule.data.parentA,
      parentB:schedule.data.parentB,
      scheduleEntriesParentA:{},
      scheduleEntriesParentB:{},
    }
  }

  const changedToDate = new Date(time);
  const onDayCopy = new Date(onDay.getTime());
  onDayCopy.setHours(12, 0, 0, 0);

  const dayDelta = Math.round((onDayCopy.getTime() - changedToDate.getTime()) / (1000 * 60 * 60 * 24));

  if (schedule.data.parentA === parentId) {

    const oldEntries = createArrayCopyAndSort(schedule.data.scheduleEntriesParentA);
    const newEntries:IScheduleEntry[] =[];
    const changedBoundaryTimes:number[] = [];

    let spinDay = new Date(onDayCopy);
    let spins = 0;
    while (spins < 156 ) {

      let newTime = new Date(spinDay);
      newTime = addDays(newTime, -dayDelta);
      newTime.setHours(changedToDate.getHours(), changedToDate.getMinutes(), changedToDate.getSeconds(), changedToDate.getMilliseconds());

      // tslint:disable-next-line: prefer-for-of
      for (let i = 0; i < oldEntries.length; i++) {
        const entry = oldEntries[i];

        if (alter === "START" && isSameDay(new Date(entry.startTime), spinDay)) {
          const newEntry = {...entry};
          
          newEntry.startAtSchool = atSchool;
          newEntry.startLoc = loc;
          newEntry.startTime = Math.min(newTime.getTime(), newEntry.endTime);
          changedBoundaryTimes.push(newEntry.startTime);

          // console.log("changed " + new Date(entry.startTime) + " to " + new Date(newEntry.startTime));
          
          newEntries.push(newEntry);
          oldEntries.splice(oldEntries.indexOf(entry), 1);
          break;
        } else if (alter === "END" && isSameDay(new Date(entry.endTime), spinDay)) {
          const newEntry = {...entry};
          
          newEntry.endAtSchool = atSchool;
          newEntry.endLoc = loc;
          newEntry.endTime = Math.max(newTime.getTime(), newEntry.startTime);
          changedBoundaryTimes.push(newEntry.endTime);

          newEntries.push(newEntry);
          oldEntries.splice(oldEntries.indexOf(entry), 1);

          // console.log("changed " + new Date(entry.endTime) + " to " + new Date(newEntry.endTime));
          break;
        }
      }
      spins += 1;
      spinDay = addDays(spinDay, 7);
    }
    newEntries.push(...oldEntries);
    newEntries.sort((a,b) => a.startTime - b.startTime);
    
    const modifiedEntries = mergeAndClean(newEntries);
    const foundEntries = findFirstAndLastEntry(schedule.data.scheduleEntriesParentB);
    const matchingEntries = preserveUnchangedMatchingBoundaries(
      makeSideToMatch(modifiedEntries, foundEntries.firstEntry, foundEntries.lastEntry),
      schedule.data.scheduleEntriesParentB,
      changedBoundaryTimes
    );
    newSchedule.data.scheduleEntriesParentA = createScheduleSide(modifiedEntries);
    newSchedule.data.scheduleEntriesParentB = createScheduleSide(matchingEntries);
  } else {
    const oldEntries = createArrayCopyAndSort(schedule.data.scheduleEntriesParentB);
    const newEntries:IScheduleEntry[] =[];
    const changedBoundaryTimes:number[] = [];

    let spinDay = new Date(onDayCopy);
    let spins = 0;
    while (spins < 156 ) {

      let newTime = new Date(spinDay);
      newTime = addDays(newTime, -dayDelta);
      newTime.setHours(changedToDate.getHours(), changedToDate.getMinutes(), changedToDate.getSeconds(), changedToDate.getMilliseconds());

      // tslint:disable-next-line: prefer-for-of
      for (let i = 0; i < oldEntries.length; i++) {
        const entry = oldEntries[i];

        if (alter === "START" && isSameDay(new Date(entry.startTime), spinDay)) {
          const newEntry = {...entry};
          
          newEntry.startAtSchool = atSchool;
          newEntry.startLoc = loc;
          newEntry.startTime = Math.min(newTime.getTime(), newEntry.endTime);
          changedBoundaryTimes.push(newEntry.startTime);

          // console.log("changed " + new Date(entry.startTime) + " to " + new Date(newEntry.startTime));
          
          newEntries.push(newEntry);
          oldEntries.splice(oldEntries.indexOf(entry), 1);
          break;
        } else if (alter === "END" && isSameDay(new Date(entry.endTime), spinDay)) {
          const newEntry = {...entry};
          
          newEntry.endAtSchool = atSchool;
          newEntry.endLoc = loc;
          newEntry.endTime = Math.max(newTime.getTime(), newEntry.startTime);
          changedBoundaryTimes.push(newEntry.endTime);

          newEntries.push(newEntry);
          oldEntries.splice(oldEntries.indexOf(entry), 1);

          // console.log("changed " + new Date(entry.endTime) + " to " + new Date(newEntry.endTime));
          break;
        }
      }
      spins += 1;
      spinDay = addDays(spinDay, 7);
    }
    newEntries.push(...oldEntries);
    newEntries.sort((a,b) => a.startTime - b.startTime);

    const modifiedEntries = mergeAndClean(newEntries);

    const foundEntries = findFirstAndLastEntry(schedule.data.scheduleEntriesParentA);
    const matchingEntries = preserveUnchangedMatchingBoundaries(
      makeSideToMatch(modifiedEntries, foundEntries.firstEntry, foundEntries.lastEntry),
      schedule.data.scheduleEntriesParentA,
      changedBoundaryTimes
    );
    newSchedule.data.scheduleEntriesParentA = createScheduleSide(matchingEntries);
    newSchedule.data.scheduleEntriesParentB = createScheduleSide(modifiedEntries); 
  }

  return newSchedule;
}

interface IBoundaryChange {
  atSchool:boolean;
  loc:string;
  time:number;
}

const applyDayvisitChanges = (schedule:ISchedule, parentId:number, eventId:number,
  start:IBoundaryChange, end:IBoundaryChange):ISchedule => {

  const newSchedule:ISchedule = {
    id:schedule.id,
    lastHandover:schedule.lastHandover,
    status:schedule.status,
    name:schedule.name,
    school:schedule.school,
    data:{
      children:schedule.data.children,
      parentA:schedule.data.parentA,
      parentB:schedule.data.parentB,
      scheduleEntriesParentA:{},
      scheduleEntriesParentB:{},
    }
  }

  if (schedule.data.parentA === parentId) {
    const modifiedEntries = mergeAndClean(modifyDayvisitEntry(createArrayCopyAndSort(schedule.data.scheduleEntriesParentA),
      eventId, start, end));
    const changedBoundaryTimes = getDayvisitBoundaryTimes(modifiedEntries, eventId);
    const foundEntries = findFirstAndLastEntry(schedule.data.scheduleEntriesParentB);
    const matchingEntries = preserveUnchangedMatchingBoundaries(
      makeSideToMatch(modifiedEntries, foundEntries.firstEntry, foundEntries.lastEntry),
      schedule.data.scheduleEntriesParentB,
      changedBoundaryTimes
    );
    newSchedule.data.scheduleEntriesParentA = createScheduleSide(modifiedEntries);
    newSchedule.data.scheduleEntriesParentB = createScheduleSide(matchingEntries);
  } else {
    const modifiedEntries = mergeAndClean(modifyDayvisitEntry(createArrayCopyAndSort(schedule.data.scheduleEntriesParentB),
      eventId, start, end));
    const changedBoundaryTimes = getDayvisitBoundaryTimes(modifiedEntries, eventId);
    const foundEntries = findFirstAndLastEntry(schedule.data.scheduleEntriesParentA);
    const matchingEntries = preserveUnchangedMatchingBoundaries(
      makeSideToMatch(modifiedEntries, foundEntries.firstEntry, foundEntries.lastEntry),
      schedule.data.scheduleEntriesParentA,
      changedBoundaryTimes
    );
    newSchedule.data.scheduleEntriesParentA = createScheduleSide(matchingEntries);
    newSchedule.data.scheduleEntriesParentB = createScheduleSide(modifiedEntries);
  }

  return newSchedule;
}

const applyDayvisitChangesToAllEntries = (schedule:ISchedule, parentId:number, originalStartDay:Date,
  originalEndDay:Date, start:IBoundaryChange, end:IBoundaryChange):ISchedule => {

  const newSchedule:ISchedule = {
    id:schedule.id,
    lastHandover:schedule.lastHandover,
    status:schedule.status,
    name:schedule.name,
    school:schedule.school,
    data:{
      children:schedule.data.children,
      parentA:schedule.data.parentA,
      parentB:schedule.data.parentB,
      scheduleEntriesParentA:{},
      scheduleEntriesParentB:{},
    }
  }

  if (schedule.data.parentA === parentId) {
    const changes = modifyRecurringDayvisitEntries(createArrayCopyAndSort(schedule.data.scheduleEntriesParentA),
      originalStartDay, originalEndDay, start, end);
    const modifiedEntries = mergeAndClean(changes.entries);
    const foundEntries = findFirstAndLastEntry(schedule.data.scheduleEntriesParentB);
    const matchingEntries = preserveUnchangedMatchingBoundaries(
      makeSideToMatch(modifiedEntries, foundEntries.firstEntry, foundEntries.lastEntry),
      schedule.data.scheduleEntriesParentB,
      changes.changedBoundaryTimes
    );
    newSchedule.data.scheduleEntriesParentA = createScheduleSide(modifiedEntries);
    newSchedule.data.scheduleEntriesParentB = createScheduleSide(matchingEntries);
  } else {
    const changes = modifyRecurringDayvisitEntries(createArrayCopyAndSort(schedule.data.scheduleEntriesParentB),
      originalStartDay, originalEndDay, start, end);
    const modifiedEntries = mergeAndClean(changes.entries);
    const foundEntries = findFirstAndLastEntry(schedule.data.scheduleEntriesParentA);
    const matchingEntries = preserveUnchangedMatchingBoundaries(
      makeSideToMatch(modifiedEntries, foundEntries.firstEntry, foundEntries.lastEntry),
      schedule.data.scheduleEntriesParentA,
      changes.changedBoundaryTimes
    );
    newSchedule.data.scheduleEntriesParentA = createScheduleSide(matchingEntries);
    newSchedule.data.scheduleEntriesParentB = createScheduleSide(modifiedEntries);
  }

  return newSchedule;
}

const applyChanges = (schedule:ISchedule, parentId:number, eventId:number, 
  alter:"START"|"END", time:number, loc:string, atSchool:boolean):ISchedule => {

  const newSchedule:ISchedule = {
    id:schedule.id,
    lastHandover:schedule.lastHandover,
    status:schedule.status,
    name:schedule.name,
    school:schedule.school,
    data:{
      children:schedule.data.children,
      parentA:schedule.data.parentA,
      parentB:schedule.data.parentB,
      scheduleEntriesParentA:{},
      scheduleEntriesParentB:{},
    }
  }


  if (schedule.data.parentA === parentId) {

    const modifiedEntries = mergeAndClean(modifyEntry(createArrayCopyAndSort(schedule.data.scheduleEntriesParentA), 
      eventId, alter, time, loc, atSchool));
    const changedBoundaryTimes = getBoundaryTimes(modifiedEntries, eventId, alter);
    const foundEntries = findFirstAndLastEntry(schedule.data.scheduleEntriesParentB);
    const matchingEntries = preserveUnchangedMatchingBoundaries(
      makeSideToMatch(modifiedEntries, foundEntries.firstEntry, foundEntries.lastEntry),
      schedule.data.scheduleEntriesParentB,
      changedBoundaryTimes
    );
    newSchedule.data.scheduleEntriesParentA = createScheduleSide(modifiedEntries);
    newSchedule.data.scheduleEntriesParentB = createScheduleSide(matchingEntries);
  } else {
    const modifiedEntries = mergeAndClean(modifyEntry(createArrayCopyAndSort(schedule.data.scheduleEntriesParentB), 
      eventId, alter, time, loc, atSchool));
    const changedBoundaryTimes = getBoundaryTimes(modifiedEntries, eventId, alter);
    const foundEntries = findFirstAndLastEntry(schedule.data.scheduleEntriesParentA);
    const matchingEntries = preserveUnchangedMatchingBoundaries(
      makeSideToMatch(modifiedEntries, foundEntries.firstEntry, foundEntries.lastEntry),
      schedule.data.scheduleEntriesParentA,
      changedBoundaryTimes
    );
    newSchedule.data.scheduleEntriesParentA = createScheduleSide(matchingEntries);
    newSchedule.data.scheduleEntriesParentB = createScheduleSide(modifiedEntries);
  }

  return newSchedule;
}

const getBoundaryTimes = (scheduleEntries:IScheduleEntry[], eventId:number, alter:"START"|"END"):number[] => {
  for (const entry of scheduleEntries) {
    if (entry.id === eventId) {
      return [alter === "START"?entry.startTime:entry.endTime];
    }
  }
  return [];
}

const getDayvisitBoundaryTimes = (scheduleEntries:IScheduleEntry[], eventId:number):number[] => {
  for (const entry of scheduleEntries) {
    if (entry.id === eventId) {
      return [entry.startTime, entry.endTime];
    }
  }
  return [];
}

const preserveUnchangedMatchingBoundaries = (matchingEntries:IScheduleEntry[],
  originalMatchingSide:{[key: string]: IScheduleEntry;}, changedBoundaryTimes:number[]):IScheduleEntry[] => {
  const originalEntries = createArrayCopyAndSort(originalMatchingSide);

  for (const entry of matchingEntries) {
    if (changedBoundaryTimes.indexOf(entry.startTime) === -1) {
      const originalStart = originalEntries.find((originalEntry) => originalEntry.startTime === entry.startTime);
      if (originalStart !== undefined) {
        entry.startAtSchool = originalStart.startAtSchool;
        entry.startLoc = originalStart.startLoc;
      }
    }

    if (changedBoundaryTimes.indexOf(entry.endTime) === -1) {
      const originalEnd = originalEntries.find((originalEntry) => originalEntry.endTime === entry.endTime);
      if (originalEnd !== undefined) {
        entry.endAtSchool = originalEnd.endAtSchool;
        entry.endLoc = originalEnd.endLoc;
      }
    }
  }

  return matchingEntries;
}

// All entries has to be in time order
const modifyEntry = (scheduleEntries:IScheduleEntry[], eventId:number, alter:"START"|"END", time:number, loc:string, atSchool:boolean) => {
  for(const entry of scheduleEntries) {
      if (entry.id === eventId) {

        if (alter === "START") {
          entry.startAtSchool = atSchool;
          entry.startLoc = loc;
          entry.startTime = Math.min(time, entry.endTime);  
        } else {
          entry.endAtSchool = atSchool;
          entry.endLoc = loc;
          entry.endTime = Math.max(time, entry.startTime);
  
        }
        break;
      } 
  }
  return scheduleEntries;
}

const modifyDayvisitEntry = (scheduleEntries:IScheduleEntry[], eventId:number, start:IBoundaryChange, end:IBoundaryChange) => {
  for(const entry of scheduleEntries) {
      if (entry.id === eventId) {
        entry.startAtSchool = start.atSchool;
        entry.startLoc = start.loc;
        entry.startTime = start.time;
        entry.endAtSchool = end.atSchool;
        entry.endLoc = end.loc;
        entry.endTime = end.time;
        break;
      } 
  }
  return scheduleEntries;
}

const modifyRecurringDayvisitEntries = (scheduleEntries:IScheduleEntry[], originalStartDay:Date,
  originalEndDay:Date, start:IBoundaryChange, end:IBoundaryChange):
  {entries:IScheduleEntry[], changedBoundaryTimes:number[]} => {

  const originalEntries = scheduleEntries.map((entry) => Object.assign({}, entry));
  const changedBoundaryTimes:number[] = [];

  changeRecurringBoundary(originalEntries, scheduleEntries, originalStartDay, "START", start, changedBoundaryTimes);
  changeRecurringBoundary(originalEntries, scheduleEntries, originalEndDay, "END", end, changedBoundaryTimes);

  scheduleEntries.sort((a,b) => a.startTime - b.startTime);

  return {entries:scheduleEntries, changedBoundaryTimes};
}

const changeRecurringBoundary = (originalEntries:IScheduleEntry[], scheduleEntries:IScheduleEntry[], onDay:Date,
  alter:"START"|"END", change:IBoundaryChange, changedBoundaryTimes:number[]) => {

  const changedToDate = new Date(change.time);
  const onDayCopy = new Date(onDay.getTime());
  onDayCopy.setHours(12, 0, 0, 0);

  const dayDelta = Math.round((onDayCopy.getTime() - changedToDate.getTime()) / (1000 * 60 * 60 * 24));

  let spinDay = new Date(onDayCopy);
  let spins = 0;
  while (spins < 156 ) {

    let newTime = new Date(spinDay);
    newTime = addDays(newTime, -dayDelta);
    newTime.setHours(changedToDate.getHours(), changedToDate.getMinutes(), changedToDate.getSeconds(), changedToDate.getMilliseconds());

    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < originalEntries.length; i++) {
      const originalEntry = originalEntries[i];
      const entry = scheduleEntries[i];

      if (alter === "START" && isSameDay(new Date(originalEntry.startTime), spinDay)) {
        entry.startAtSchool = change.atSchool;
        entry.startLoc = change.loc;
        entry.startTime = newTime.getTime();
        changedBoundaryTimes.push(entry.startTime);
        break;
      } else if (alter === "END" && isSameDay(new Date(originalEntry.endTime), spinDay)) {
        entry.endAtSchool = change.atSchool;
        entry.endLoc = change.loc;
        entry.endTime = newTime.getTime();
        changedBoundaryTimes.push(entry.endTime);
        break;
      }
    }
    spins += 1;
    spinDay = addDays(spinDay, 7);
  }
}


const quickSwap = (side:IScheduleEntry[], year:number,month:number,date:number, swap:"MORNING"|"NIGHT"):IScheduleEntry[] => {

  let id = 1;
  if (side.length > 0) {
    id = side.reduce((prev, current) =>  ((prev && prev.id > current.id) ? prev : current)).id+1;
  }

  let swapDate = new Date(year,month,date,0,0,0,1);
  if (swap === "NIGHT") {
    swapDate = addDays(swapDate, 1);
  }
  const swapTime:number = swapDate.getTime();
  const beforeSwap = swapTime - (1000*60*60*24);
  const afterSwap = swapTime + (1000*60*60*24);

  const newSide:IScheduleEntry[] = [];

  let haveFoundSpot = false;

  let prevEntry:IScheduleEntry|undefined;
  for(const entry of side) {
    if (entry.startTime < swapTime && entry.endTime > swapTime) { // modify (possibly split) this entry
      haveFoundSpot = true;

      if (entry.startTime > beforeSwap && entry.endTime < afterSwap) { // remove this entry
        prevEntry = entry;
        continue;        
      } else if (entry.startTime < beforeSwap && entry.endTime < afterSwap) { // change endTime
        const original:IScheduleEntry = JSON.parse(JSON.stringify(entry));
        entry.endTime = (addDays(new Date(entry.endTime), -1)).getTime(); // move end time one day earlier
        newSide.push(entry);        
        prevEntry = original;
      } else if (entry.startTime > beforeSwap && entry.endTime > afterSwap) { // change endTime
        entry.startTime = (addDays(new Date(entry.startTime), 1)).getTime(); // move start time one day later
        newSide.push(entry);
        prevEntry = entry;
      } else { // we have to break up this entry so lets make a hole in it

        const prevEndDate = new Date(entry.endTime);
        const prevStartDate = new Date(entry.startTime);
        const holeStartDate = new Date(swapTime-(1000*60*60*12));
        const holeEndDate = new Date(swapTime+(1000*60*60*12));

        holeStartDate.setHours(prevEndDate.getHours());
        holeStartDate.setMinutes(prevEndDate.getMinutes());
        holeStartDate.setSeconds(0);
        holeStartDate.setMilliseconds(0);
  
        holeEndDate.setHours(prevStartDate.getHours());
        holeEndDate.setMinutes(prevStartDate.getMinutes());
        holeEndDate.setSeconds(0);
        holeEndDate.setMilliseconds(0);

        const newEntry:IScheduleEntry = {
          id,
          startTime:holeEndDate.getTime(),
          startLoc:entry.startLoc,
          startAtSchool:entry.startAtSchool,
          endTime:entry.endTime,
          endLoc:entry.endLoc,
          endAtSchool:entry.endAtSchool,
        }

        id += 1;

        entry.endTime = holeStartDate.getTime();

        newSide.push(entry);
        newSide.push(newEntry);
        prevEntry = newEntry;
      }

    } else if (prevEntry !== undefined && prevEntry.endTime < swapTime && entry.startTime > swapTime) { // modify (possibly split) between entries
      haveFoundSpot = true;

      if (prevEntry.endTime > beforeSwap && entry.startTime < afterSwap) { // merge prev entry and this entry
        prevEntry.endTime = entry.endTime;
        prevEntry.endAtSchool = entry.endAtSchool;
        prevEntry.endLoc = entry.endLoc;
        prevEntry = entry;
        continue;
      } else if (prevEntry.endTime > beforeSwap) { // move previous end time one day later
        prevEntry.endTime = (addDays(new Date(prevEntry.endTime), 1)).getTime(); 
        newSide.push(entry);
        prevEntry = entry;
      } else if (entry.startTime < afterSwap) {  // move next start time one day earlier
        entry.startTime = (addDays(new Date(entry.startTime), -1)).getTime();
        newSide.push(entry);
        prevEntry = entry;
      } else { // we have to add a new entry
        const prevEndDate = new Date(prevEntry.endTime);
        const prevStartDate = new Date(entry.startTime);
        const newEntryStartDate = new Date(swapTime-(1000*60*60*12));
        const newEntryEndDate = new Date(swapTime+(1000*60*60*12));

        newEntryStartDate.setHours(prevStartDate.getHours());
        newEntryStartDate.setMinutes(prevStartDate.getMinutes());
        newEntryStartDate.setSeconds(0);
        newEntryStartDate.setMilliseconds(0);

        newEntryEndDate.setHours(prevEndDate.getHours());
        newEntryEndDate.setMinutes(prevEndDate.getMinutes());
        newEntryEndDate.setSeconds(0);
        newEntryEndDate.setMilliseconds(0);
  

        const newEntry:IScheduleEntry = {
          id,
          startTime:newEntryStartDate.getTime(),
          startLoc:entry.startLoc,
          startAtSchool:entry.startAtSchool,
          endTime:newEntryEndDate.getTime(),
          endLoc:prevEntry.endLoc,
          endAtSchool:prevEntry.endAtSchool,
        }

        id += 1;

        newSide.push(newEntry);
        newSide.push(entry);
        prevEntry = entry;
      }

    } else if (entry.startTime > swapTime && prevEntry === undefined) { // extend the start of the schedule
      haveFoundSpot = true;
      let startDate = new Date(entry.startTime);
      while (startDate.getTime() > swapTime) {
        startDate = addDays(startDate, -1);
      }
      entry.startTime = startDate.getTime();
      newSide.push(entry);
    } else {
      newSide.push(entry);
      prevEntry = entry;
    }
  }
  if (!haveFoundSpot && prevEntry !== undefined && prevEntry.endTime < swapTime) { // extend the end of the schedule
    if (prevEntry.endTime < swapTime - (1000*60*60*24)) { // lets extend the last
      let endDate = new Date(prevEntry.endTime);
      endDate = addDays(endDate, 1);
      prevEntry.endTime = endDate.getTime();
    } else { // lets add a new entry
      const startDate = new Date(swapTime-(1000*60*60*12));
      const endDate = new Date(swapTime+(1000*60*60*12));

      startDate.setHours(12);
      startDate.setMinutes(0);
      startDate.setSeconds(0);
      startDate.setMilliseconds(0);

      endDate.setHours(12);
      endDate.setMinutes(0);
      endDate.setSeconds(0);
      endDate.setMilliseconds(0);

      newSide.push({
        id,
        endAtSchool:false,
        endTime: endDate.getTime(),
        startAtSchool:false,
        startTime: startDate.getTime(),
      });

      id+=1;
    }
  }

  newSide.sort((a,b) => a.startTime - b.startTime);
  return newSide;
}

export { applyChanges, applyDayvisitChanges, applyDayvisitChangesToAllEntries, hasChildAtTime, addQuickSleep, swapParent,
  shiftSpecificDay, moveLastEntry, applyToAllEntries };



// WEBPACK FOOTER //
// ./src/components/calendar2/editPane/editActions/QuickChange/QuickChangeCalc.ts